import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

/**
 * HeroScoreShield — replaces the plain gauge with a tier-colored ring that
 * feels like a game HUD. Score 0..100 maps to tiers:
 *   0-25   Bronze
 *   26-50  Silver
 *   51-75  Gold
 *   76-100 Legendary (emerald aura)
 *
 * Renders the score number, tier label, and a "stronger than X%" percentile.
 */

function tierFor(score) {
  if (score >= 76) {
    return {
      key: "legendary",
      label: "Legendary",
      ring: "#22E07A",
      glow: "0 0 22px rgba(34,224,122,0.65), 0 0 40px rgba(34,224,122,0.35)",
      accent: "#22E07A",
      halo: "from-[#22E07A]/25 via-[#22E07A]/5 to-transparent",
    };
  }
  if (score >= 51) {
    return {
      key: "gold",
      label: "Excellent",
      ring: "#F5D849",
      glow: "0 0 18px rgba(245,216,73,0.55)",
      accent: "#F5D849",
      halo: "from-[#F5D849]/25 via-[#F5D849]/5 to-transparent",
    };
  }
  if (score >= 26) {
    return {
      key: "silver",
      label: "Rising",
      ring: "#CBD5E1",
      glow: "0 0 12px rgba(203,213,225,0.45)",
      accent: "#CBD5E1",
      halo: "from-slate-300/20 via-slate-300/5 to-transparent",
    };
  }
  return {
    key: "bronze",
    label: "Recruit",
    ring: "#C97B4A",
    glow: "0 0 12px rgba(201,123,74,0.55)",
    accent: "#C97B4A",
    halo: "from-orange-500/20 via-orange-500/5 to-transparent",
  };
}

// The percentile framing is a coaching cue, not a leaderboard. Deterministic
// mapping: higher score = higher perceived rank. Keeps copy honest without
// exposing raw distribution data.
function percentileFor(score) {
  if (score >= 90) return 95;
  if (score >= 80) return 87;
  if (score >= 70) return 74;
  if (score >= 60) return 61;
  if (score >= 50) return 48;
  if (score >= 40) return 36;
  if (score >= 30) return 24;
  if (score >= 20) return 15;
  return 8;
}

export default function HeroScoreShield({ score = 0, size = 200, onInfoClick }) {
  const target = Math.max(0, Math.min(100, Math.round(score)));
  const [displayed, setDisplayed] = useState(target);
  const rafRef = useRef();
  const displayedRef = useRef(target);
  displayedRef.current = displayed;

  useEffect(() => {
    const start = performance.now();
    const from = displayedRef.current;
    const dur = 900;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setDisplayed(from + (target - from) * ease(t));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  const tier = tierFor(target);
  const stroke = 12;
  const radius = (size - stroke - 6) / 2;
  const circ = 2 * Math.PI * radius;
  const dashOffset = circ * (1 - target / 100);

  return (
    <div
      className="rounded-3xl bg-[#0B221D] border border-[#1B4238] p-5 flex flex-col items-center relative overflow-hidden"
      data-testid="hero-score-shield"
    >
      <div className={`absolute inset-0 bg-gradient-to-b ${tier.halo} pointer-events-none`} aria-hidden />
      <div className="w-full flex items-start justify-between relative">
        <p
          className="text-[11px] font-black uppercase tracking-[0.2em] text-[#F5D849]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Hero Score
        </p>
        {onInfoClick && (
          <button
            type="button"
            onClick={onInfoClick}
            className="text-[#93B1A8] hover:text-white transition-colors"
            data-testid="hero-score-info"
            aria-label="How is Hero Score calculated?"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div
        className="relative mt-3"
        style={{ width: size, height: size }}
        role="meter"
        aria-valuenow={target}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Hero Score ${target} out of 100 — tier ${tier.label}`}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
          style={{ filter: `drop-shadow(${tier.glow})` }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#0F2E27"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={tier.ring}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            style={{
              strokeDashoffset: dashOffset,
              transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)",
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span
            className="text-[42px] font-black text-white leading-none tabular-nums"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="hero-score-value"
          >
            {Math.round(displayed)}
          </span>
          <span
            className="text-[11px] font-black uppercase tracking-[0.18em] mt-1"
            style={{ color: tier.accent, fontFamily: "Outfit, sans-serif" }}
            data-testid="hero-score-tier"
          >
            {tier.label}
          </span>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-[#C7DAD3] text-center leading-snug px-2 relative">
        You&rsquo;re stronger than <span className="font-extrabold text-white">{percentileFor(target)}%</span> of Money Heroes!
      </p>
    </div>
  );
}
